<template>
  <div class="min-h-screen" data-theme="amber">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="shops" />
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a :href="'/shops'" class="text-sm text-blue-700 hover:text-blue-900 mb-6 inline-block">&larr; {{ t('shops.back_to_list') }}</a>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div class="flex items-center gap-3 mb-4">
          <img v-if="shop.logo_url" :src="shop.logo_url" class="w-16 h-16 rounded-lg object-cover border border-gray-200 shrink-0" />
          <h1 class="text-2xl font-bold text-gray-900">{{ shop.name }}</h1>
          <span v-if="shop.category" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {{ t('shops.category_' + shop.category) }}
          </span>
        </div>

        <div v-if="shop.pending && userAdmin" class="mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 mr-2">{{ t('shops.pending') }}</span>
          <button @click="approveItem" class="px-4 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition">{{ t('shops.approve') }}</button>
        </div>
        <div v-else-if="shop.pending" class="mb-4">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">{{ t('shops.pending') }}</span>
        </div>

        <div v-if="shop.brands && shop.brands.length > 0" class="mb-6">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">{{ t('shops.brands') }}</h3>
          <div class="flex flex-wrap gap-2">
            <span v-for="b in shop.brands" :key="b" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{{ b }}</span>
          </div>
        </div>

        <p v-if="shop.description" class="text-gray-600 mb-6 whitespace-pre-line">{{ shop.description }}</p>

        <a v-if="shop.link" :href="shop.link" target="_blank"
           class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition mb-6">
          {{ t('shops.visit_link') }}
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
        </a>

        <!-- Locations -->
        <div v-if="locationsWithCoords.length > 0" class="mb-6">
          <h3 v-if="locationsWithCoords.length > 1" class="text-sm font-semibold text-gray-700 mb-2">{{ t('shops.locations') }} ({{ locationsWithCoords.length }})</h3>
          <div v-if="locationsWithCoords.length > 1" class="flex flex-wrap gap-2 mb-3">
            <span v-for="loc in locationsWithCoords" :key="loc.id" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
              {{ [loc.name, loc.address].filter(Boolean).join(' — ') || t('shops.location') }}
            </span>
          </div>
          <div class="rounded-lg border border-gray-200 overflow-hidden map-container">
            <div ref="mapContainer" style="height: 300px; width: 100%;"></div>
          </div>
        </div>

        <div v-if="userAdmin || shop.user_id === userId" class="flex gap-3 mt-4">
          <a :href="`/shops?edit=${shop.id}`" class="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">{{ t('shops.edit') }}</a>
          <button @click="deleteItem" class="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">{{ t('shops.delete') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png', iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png' })

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, shop: Object, csrfToken: String })
const { t, locale } = useI18n()
const shop = reactive({ ...props.shop })
const mapContainer = ref(null)

const locationsWithCoords = computed(() => (shop.locations || []).filter(l => l.latitude && l.longitude))

async function approveItem() {
  try {
    const res = await fetch(`/shops/${shop.id}/approve`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) shop.pending = false
  } catch {}
}

async function deleteItem() {
  if (!confirm(t('shops.confirm_delete_message'))) return
  try {
    const res = await fetch(`/shops/${shop.id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) window.location.href = '/shops'
  } catch {}
}

onMounted(() => {
  if (locationsWithCoords.value.length > 0 && mapContainer.value) {
    const first = locationsWithCoords.value[0]
    const map = L.map(mapContainer.value).setView([parseFloat(first.latitude), parseFloat(first.longitude)], 13)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map)

    const markers = []
    locationsWithCoords.value.forEach(loc => {
      const marker = L.marker([parseFloat(loc.latitude), parseFloat(loc.longitude)]).addTo(map)
      if (loc.name) marker.bindPopup(`<strong>${loc.name}</strong>`)
      markers.push(marker)
    })

    if (markers.length > 1) map.fitBounds(L.featureGroup(markers).getBounds().pad(0.2))
    setTimeout(() => map.invalidateSize(), 100)
  }
})
</script>
